import { useCallback, useState } from 'react';
import { queryClient, queryKeys } from './query-client';
import { revokeFileShare, shareFile, useFileShares, type FileSharesState } from './file-shares';
import {
  cancelConfirm,
  initialShareDialogState,
  removeShare,
  shareNotices,
  submitShare,
  type GrantOutcome,
  type ManyGrantResult,
  type ShareAttempt,
  type ShareDialogState,
  type ShareFlowDeps,
} from './share-flow';
import { confirmAppliesTo, planGrants, releaseRest, type FirstGrantOutcome } from './recipient-field';

// Binds the framework-free share machine (lib/share-flow.ts) to React state and the real data layer.
// Everything decidable without a DOM lives there and is tested there; this file is wiring only, which
// is why it holds no branching of its own.
//
// The sibling of `use-app-share.ts`, minus lending: an app can borrow the owner's tools, a file
// grants exactly itself.

export interface FileShareFlow {
  state: ShareDialogState;
  /** The people list. Its `error` branch renders as "couldn't load", NEVER as private. */
  shares: FileSharesState;
  /**
   * Retire the last attempt's verdict, without touching an open question.
   *
   * Replaces the old `setEmail`, which the recipient field made dead: the dialog no longer holds one
   * address, so "the owner is typing a new one" is now "the owner edited the list". The QUESTION
   * deliberately survives — `confirmAppliesTo` is what keeps a stored yes honest across an edit, and
   * destroying the question instead is the bug both phones had.
   */
  clearError: () => void;
  /** `confirmUnknown` answers the "nobody answers to that address" question with yes. */
  submit: (confirmUnknown?: boolean) => Promise<void>;
  /**
   * Share with several people in one press.
   *
   * Answers the identifiers that were NOT granted, so the dialog can leave exactly those on screen.
   * It stops at the first one that raises a question — nothing was written for that person and the
   * owner has to answer before anything else happens, and carrying on would bury the question under
   * three more results.
   */
  submitMany: (identifiers: string[], confirmUnknown?: boolean) => Promise<ManyGrantResult>;
  /** The owner said no. Nothing was written, so this only clears the question. */
  cancelConfirm: () => void;
  remove: (granteeRecipientId: string) => Promise<void>;
  /** The retry offered under a FAILED load — re-runs the read, nothing else. */
  retry: () => void;
}

/**
 * `filePath` is the file's full `workspaces` key (`<workspaceId>/<name>`), and is sent on every
 * share alongside whatever id the row had. It is what lets a file written before the id field
 * existed be shared at all — the server resolves it once and mints an id. The grant is still keyed
 * on the id, so this does not make access follow a path.
 */
export function useFileShare(fileId: string | undefined, filePath: string): FileShareFlow {
  const [state, setState] = useState<ShareDialogState>(initialShareDialogState);
  // The id the FIRST share of a pre-id file minted. The dialog's own `fileId` prop cannot learn it —
  // the file list behind it is a cached read from before the share — and everything else this hook
  // does is keyed on an id, so without holding it the dialog would report "Private" about the person
  // it had just shared with, and offer no way to take that access back.
  const [minted, setMinted] = useState<string | undefined>(undefined);
  const subject = fileId || minted;
  const shares = useFileShares(subject);

  const reload = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.fileShares(subject) });
  }, [subject]);

  const deps: ShareFlowDeps = {
    share: async (id, email, confirmUnknown) => {
      const outcome = await shareFile(id, filePath, email, confirmUnknown);
      if (!fileId && outcome.fileId) setMinted(outcome.fileId);
      // Every field passed through, none swallowed. `requiresConfirmation` in particular MUST reach
      // the machine: it means nothing was written, and a dropped one renders as a share that worked.
      return {
        granteeIsNew: outcome.share?.granteeIsNew,
        requiresConfirmation: outcome.requiresConfirmation,
        ...(typeof outcome.granteeNotified === 'boolean' ? { granteeNotified: outcome.granteeNotified } : {}),
        // Suppresses the could-not-tell-them line on a REPEAT share, which announces nothing by
        // design. Without it the file dialog sends the owner off to inform somebody who was
        // messaged when the grant was made — the bug the app side fixed in its own copy first.
        alreadyShared: outcome.alreadyShared,
        shareLink: outcome.shareLink,
      };
    },
    revoke: revokeFileShare,
    setState,
    reload,
  };

  // One recipient's grant, with its own notices captured. `submitShare` writes the single-attempt
  // state as it always has (so a one-person share is untouched); the spy sits on the RPC result and
  // reads it through the SAME `shareNotices` derivation, so the two cannot disagree.
  const grantOne = async (
    subjectId: string,
    identifier: string,
    base: ShareDialogState,
    confirmUnknown: boolean,
  ): Promise<{ attempt: ShareAttempt; outcome: GrantOutcome | null }> => {
    let outcome: GrantOutcome | null = null;
    const spy: ShareFlowDeps = {
      ...deps,
      share: async (id, email, confirm) => {
        const result = await deps.share(id, email, confirm);
        if (!result?.requiresConfirmation) outcome = { identifier: email, ...shareNotices(result) };
        return result;
      },
    };
    const attempt = await submitShare(subjectId, { ...base, email: identifier }, spy, confirmUnknown);
    return { attempt, outcome: attempt === 'shared' ? outcome : null };
  };

  return {
    state,
    shares,
    clearError: () => setState((prev) => (prev.error ? { ...prev, error: null } : prev)),
    submit: async (confirmUnknown = false) => {
      // No id yet is the ORDINARY case for a file older than the field, and the server mints one
      // from the path — so the subject the machine carries falls back to the path rather than the
      // submit being refused. Before this, a file with no id could not be shared at all.
      await submitShare(subject ?? '', state, deps, confirmUnknown);
    },
    submitMany: async (identifiers, confirmUnknown = false) => {
      const plan = planGrants(identifiers, 'file', subject);
      if (!plan) return { failed: [], outcomes: [] };

      // The FIRST grant runs alone whenever the file has no id yet: it is the one that mints it, and
      // a second concurrent share would mint a different one and orphan this grant. `releaseRest`
      // decides from what the first attempt actually DID — a confirmation question wrote nothing and
      // minted nothing, so it must not release the rest.
      //
      // The stored yes is honoured only while the run still BEGINS with the address it was given
      // about; the list stays editable under the question, and a transferred yes bypasses the
      // unknown-address gate for somebody nobody asked about.
      const confirmed = confirmUnknown && confirmAppliesTo(state.confirmEmail, plan.first);
      const first = await grantOne(subject ?? '', plan.first, state, confirmed);
      const outcome: FirstGrantOutcome =
        first.attempt === 'shared' ? 'wrote' : first.attempt === 'question' ? 'askedFirst' : 'failed';
      const outcomes = first.outcome ? [first.outcome] : [];
      if (!releaseRest(plan, outcome) || outcome !== 'wrote') return { failed: identifiers, outcomes };

      // Sequential rather than parallel even once the id exists: each attempt writes the shared
      // dialog state, and N concurrent writers would leave the last one's notice on screen for
      // everybody. The list is a handful of people, so the cost is nothing.
      for (let i = 0; i < plan.rest.length; i += 1) {
        const next = await grantOne(subject ?? '', plan.rest[i], initialShareDialogState, false);
        if (next.outcome) outcomes.push(next.outcome);
        // Everything from here stays on screen: a question needs answering and a failure needs
        // retrying before the rest can go, and nothing after this index has been attempted at all.
        if (next.attempt !== 'shared') return { failed: plan.rest.slice(i), outcomes };
      }
      return { failed: [], outcomes };
    },
    cancelConfirm: () => cancelConfirm(state, deps),
    remove: async (granteeRecipientId: string) => {
      if (!subject) return;
      await removeShare(subject, granteeRecipientId, state, deps);
    },
    retry: reload,
  };
}
